import { useLayoutEffect, useRef, useState } from 'react';

/**
 * A figure that RESOLVES as it arrives, instead of appearing already finished.
 *
 * Design system § 9 gives the `expressive` step — 420ms — to "feedback,
 * route/step transitions", and says of progress that it "grows from the left;
 * never animates backwards". This is that rule spent on a number rather than on
 * a bar: the total on the profile is something the learner built up a session at
 * a time, and a figure that counts up to it says so in the only language a
 * single number has.
 *
 * IT IS NOT A LOOP AND NOT AN AMBIENT GESTURE. It runs once, on arrival, for the
 * length of a route transition, and then the number simply is the number —
 * § 4.1's "never simultaneous with user-driven motion" holds because the
 * animation is over before there is anything for it to be simultaneous with.
 *
 * THE DURATION IS THE TOKEN'S, read off the document rather than copied here, so
 * `--motion-expressive` stays the one place that value lives.
 *
 * Reduced motion paints the target and never counts. No meaning is lost: the
 * figure is the meaning, and the counting was never carrying any of it.
 */

/** § 9's `expressive`, for the case where the document cannot answer. */
const FALLBACK_MS = 420;

function expressiveMs(): number {
  const declared = getComputedStyle(document.documentElement)
    .getPropertyValue('--motion-expressive')
    .trim();
  const ms = Number.parseFloat(declared);
  return Number.isFinite(ms) && ms > 0 ? ms : FALLBACK_MS;
}

/**
 * Ease-out cubic — the JavaScript reading of § 9's `cubic-bezier(.2, .8, .2, 1)`.
 *
 * Not the bezier itself, which would need a solver for a curve nobody can tell
 * apart from this one at 420ms: both leave fast, both settle slowly, and their
 * largest disagreement across the run is a couple of percent of a figure that is
 * rounded to an integer before it is drawn. The easing is here at all because a
 * LINEAR count reads as a mechanical odometer, which is the one thing § 1.1
 * ("optimistic, never anxious") rules out.
 */
function easeOut(t: number): number {
  return 1 - (1 - t) ** 3;
}

export function useCountUp(target: number): number {
  /*
   * THE TARGET IS THE INITIAL STATE, not zero. Every path that declines to
   * animate — reduced motion, a figure of zero, a browser that never ran the
   * effect — therefore lands on the real number, and "0 pontos" is the one
   * wrong figure this hook could possibly leave on screen. The layout effect
   * below is what rewinds it, and it runs BEFORE the first paint, so the
   * rewind is never seen.
   */
  const [value, setValue] = useState(target);
  const frame = useRef(0);

  useLayoutEffect(() => {
    if (target <= 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return;
    }

    setValue(0);

    const duration = expressiveMs();
    const started = performance.now();

    const step = (now: number) => {
      const elapsed = now - started;
      if (elapsed >= duration) {
        setValue(target);
        return;
      }

      setValue(Math.round(target * easeOut(elapsed / duration)));
      frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [target]);

  return value;
}
